<script lang="ts">
  import { orgPlus } from "@nrk/origo";
  import type { Collection } from "bildebanken-model";
  import { useSelector } from "@xstate/svelte";
  import { onDestroy } from "svelte";
  import { createMimirCollectionQuery } from "state/queries/mimir";
  import { getCollectionItemsFromQuery } from "state/queries";
  import { getErrorMessage } from "utils/fns";
  import {
    collectionsPageSelectionService,
    getSelectedIds,
    getSelectedItems,
    isItemSelected,
    resetSelections,
  } from "state/SelectionService";
  import { itemOnKeydown } from "components/SearchPage/keyboardInteraction";
  import CollectionTile from "./CollectionTile.svelte";
  import CreateCollectionDialog from "./CreateCollectionDialog.svelte";
  import { createCollectionTileHandler } from "./collectionTileHandler";
  import EditCollectionDialog from "./EditCollectionDialog.svelte";
  import ShareCollectionDialog from "./ShareCollectionDialog.svelte";
  import DeleteCollectionDialog from "./DeleteCollectionDialog.svelte";
  import { returnToSearchPage } from "state/page";
  import { setFolderFilter } from "services/folders/func";
  import { notify } from "services/notifications";
  import { addUserToCollection } from "services/collections/api";
  import { getCurrentUser } from "../../services/api";
  import { removeUserFromCollectionAndUpdateCache } from "./collectionOperations";
  import { useQueryClient } from "@tanstack/svelte-query";
  import { isPlugin } from "state/app";

  const queryClient = useQueryClient();

  const collectionsQuery = createMimirCollectionQuery({
    sortProperty: "title",
    sortOrder: "asc",
  });

  async function removeUserFromCollection(collectionId: string, userId: string) {
    return await removeUserFromCollectionAndUpdateCache(queryClient, collectionId, userId);
  }
  export let sharedCollectionId: string | undefined = undefined;
  let gettingSharedData: boolean = false;
  if (sharedCollectionId) {
    (async () => {
      try {
        gettingSharedData = true;
        const success = await addUserToCollection(sharedCollectionId);
        if (success) {
          returnToSearchPage();
          await setFolderFilter(sharedCollectionId);
          notify("Du har nå fått tilgangen til den delte samlingen", "success");
        } else {
          console.error("Failed to add user to collection");
          notify("Kunne ikke legge deg til som bruker av den delte samlingen", "error", 5000);
        }
      } catch (error) {
        console.error("Error adding user to collection:", error);
        notify("Kunne ikke legge deg til som bruker av den delte samlingen", "error", 5000);
      } finally {
        gettingSharedData = false;
      }
    })();
  }

  const currentUser = getCurrentUser();
  const collections = getCollectionItemsFromQuery(collectionsQuery);
  let allCollections: Collection[] = [];
  $: allCollections = $collections;
  $: myCollections = allCollections.filter((c) => c.owner === currentUser.id);
  $: sharedCollections = allCollections.filter((c) => c.owner !== currentUser.id);

  const handler = createCollectionTileHandler({
    selectionService: collectionsPageSelectionService,
  });

  const onKeyDown = itemOnKeydown({
    selectionService: collectionsPageSelectionService,
    openDetailsForItem: () => {},
  });

  function updateSelectionItems(folders: Collection[]) {
    collectionsPageSelectionService.send({
      type: "UPDATE_ITEMS",
      itemIds: folders.map((f) => f.id),
    });
  }

  $: updateSelectionItems(allCollections);

  $: selectedIds = getSelectedIds($collectionsPageSelectionService);
  $: selectedItems = getSelectedItems(selectedIds, allCollections);

  onDestroy(() => {
    resetSelections(collectionsPageSelectionService);
  });
</script>

<div class="collections" class:isEmbedded={isPlugin}>
  {#if gettingSharedData}
    <div class="org-spinner"></div>
  {:else}
    <div class="header">
      <h2>Samlinger</h2>
      <button class="org-button org-button--suggestive" data-for="create-collection-dialog"
        >{@html orgPlus} Opprett ny samling</button
      >
    </div>
    {#if $collectionsQuery.isLoading}
      Henter samlinger...
    {:else if $collectionsQuery.isError}
      <p>Feil ved henting av samlinger: {getErrorMessage($collectionsQuery.error)}</p>
    {:else}
      <h3>Mine samlinger</h3>
      <div class="collection-container">
        {#each myCollections as collection}
          <CollectionTile
            folderId={collection.id}
            ownerId={collection.owner}
            visibleTo={collection.visibleTo}
            name={collection.name || "Henter navn..."}
            selected={useSelector(collectionsPageSelectionService, isItemSelected(collection.id))}
            {handler}
            on:keydown={onKeyDown}
          />
        {/each}
      </div>
      <h3 class="shared-collections-header">Delte samlinger</h3>
      <div class="collection-container">
        {#each sharedCollections as collection}
          <CollectionTile
            folderId={collection.id}
            ownerId={collection.owner}
            visibleTo={collection.visibleTo}
            name={collection.name || "Henter navn..."}
            selected={useSelector(collectionsPageSelectionService, isItemSelected(collection.id))}
            {handler}
            on:keydown={onKeyDown}
          />
        {/each}
      </div>
    {/if}

    <CreateCollectionDialog />
    <EditCollectionDialog collectionId={selectedIds[0]} />
    <DeleteCollectionDialog collection={selectedItems[0]} />
    <ShareCollectionDialog collection={selectedItems[0]} {currentUser} {removeUserFromCollection} />
  {/if}
</div>

<style>
  .collection-container {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: var(--org-xsmall);
  }

  .collections {
    margin: var(--org-medium);
  }

  .shared-collections-header {
    margin-top: 2.5rem;
  }

  .collections.isEmbedded {
    width: 100%;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  h2 {
    margin-bottom: var(--org-medium);
  }
</style>
